import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { MatSnackBarModule } from '@angular/material/snack-bar';

// Removed deprecated InfiniteScrollModule - using modern Intersection Observer API instead
import { filter } from 'rxjs/operators';
import { DynamicFooterComponent } from 'src/app/components/dynamic-footer/dynamic-footer.component';
import { DynamicHeaderComponent } from 'src/app/components/dynamic-header/dynamic-header.component';
import { PushNotificationPanelComponent } from 'src/app/components/push-notification/push-notification-panel.component';
import { HomePageRenderService } from 'src/app/home/services/home-page-render.service';
import { AuthenticationRepositoryService } from './authentication/services/authentication-repository.service';
import { BrowserApiService } from './core/services/browser-api/browser-api.service';
import { CommunicationService } from './core/services/communication/communication.service';
import { FaviconService } from './core/services/favicon/favicon.service';
import { GlobalScrollService } from './core/services/global-scroll/global-scroll.service';
import { InjectScriptsService } from './core/services/inject-scripts/inject-scripts.service';
import { TitleService } from './core/services/page-title/title.service';
import { PlatformService } from './core/services/platform/platform.service';
import { PushNotificationsService } from './core/services/push-notifications/push-notifications.service';
import { SeoService } from './core/services/seo/seo.service';
import { StoreConfigurationRepositoryService } from './core/services/store-configuration/store-configuration-repository.service';
import { DEFAULT_THEME, ThemeService } from './core/services/theme/theme.service';

import { AdsManagementService } from 'src/app/core/services/adsManagement/ads-management.service';
import { SmartPreloadingStrategy } from './core/strategies/smart-preloading.strategy';

@Component({
  selector: 'webstore-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSnackBarModule,
    DynamicHeaderComponent,
    DynamicFooterComponent,
    PushNotificationPanelComponent
  ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private themeService = inject(ThemeService);
  private platformService = inject(PlatformService);
  private browserApiService = inject(BrowserApiService);
  private authenticationRepositoryService = inject(AuthenticationRepositoryService);
  private titleService = inject(TitleService);
  private router = inject(Router);
  private globalScrollService = inject(GlobalScrollService);
  private seoService = inject(SeoService);
  private faviconService = inject(FaviconService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  private injectScriptsService = inject(InjectScriptsService);
  private renderer = inject(Renderer2);
  pushNotificationsService = inject(PushNotificationsService);
  private homePageRenderService = inject(HomePageRenderService);
  private communicationService = inject(CommunicationService);
  private adsManagementService = inject(AdsManagementService);
  private smartPreloadingStrategy = inject(SmartPreloadingStrategy);

  @ViewChild('header') header!: DynamicHeaderComponent; // Keep for ViewChild
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;

  private _isScrolled = signal(false);
  readonly isScrolled = this._isScrolled.asReadonly();

  private _isBrowser = signal(false);
  readonly isBrowser = this._isBrowser.asReadonly();

  // Modern infinite scroll implementation using Intersection Observer
  private intersectionObserver?: IntersectionObserver;
  private readonly scrollThreshold = 100; // pixels from bottom to trigger load
  private preloadTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Computed signals for derived state - directly from services
  readonly title = computed(() => this.titleService.currentTitle() || '');
  readonly hasTitle = computed(() => !!this.title());
  readonly showScrollButton = computed(() => this.isScrolled());

  // Additional computed signals for better reactivity
  readonly storeConfig = computed(() => this.storeConfigurationRepositoryService.storeConfigurations());
  readonly seoConfig = computed(() => this.storeConfigurationRepositoryService.seoConfiguration());
  readonly faviconPath = computed(() => this.storeConfigurationRepositoryService.faviconPath());
  readonly headerScripts = computed(() => this.storeConfig()?.header_tags?.script || '');
  readonly isStoreReady = computed(() => !!this.storeConfig());
  readonly currentRoute = computed(() => this.router.url);


  constructor() {

    // Use computed signals for reactive updates
    effect(() => {
      if (this.isStoreReady()) {
        this.seoService.updateMetaTags(this.headerScripts());
      }
    });

    // Use computed signal for route changes
    effect(() => {
      const currentUrl = this.currentRoute();
      if (currentUrl) {
        this.getUtmAndCodeDetailsFromUrl();
      }
    });

    // Watch for store configuration changes and load theme when ready
    effect(() => {
      const storeConfig = this.storeConfig();
      if (storeConfig) {
        // Load store configuration theme (global theme variables)
        const colorConfig = this.storeConfigurationRepositoryService.colorConfigurations();
        const fontConfig = this.storeConfigurationRepositoryService.fontConfigurations();

        // ✅ Always set font variables (use DEFAULT_FONT if fontConfig is null)
        this.themeService.setFontVariables(fontConfig, this.renderer);

        // ✅ Always set color theme (use DEFAULT_THEME if colorConfig is null)
        this.themeService.setThemeVariables(colorConfig || DEFAULT_THEME, this.renderer);
      }
    });


    // Watch for favicon changes reactively
    // ✅ SSR FIX: Remove isBrowser() check so favicon is set during SSR
    // FaviconService works in SSR because DOCUMENT token is available
    effect(() => {
      const faviconPath = this.faviconPath();
      if (faviconPath) {
        this.faviconService.setFavicon(faviconPath);
      }
    });

    // Initialize theme with proper guard to prevent infinite loops
    let themeInitialized = false;
    effect(() => {
      const storeConfig = this.storeConfig();
      if (storeConfig?.home_page?.published_page_contents && !themeInitialized) {
        themeInitialized = true;
        const renderResult = this.homePageRenderService.renderHomePage(this.renderer);

      }
    });

    // Cleanup infinite scroll observer on destroy
    effect(() => {
      this.destroyRef.onDestroy(() => {
        this.stopInfiniteScrollObservation();
      });
    });

    // Watch for SEO configuration changes reactively
    // ✅ SSR FIX: Remove isBrowser() check so SEO meta tags are set during SSR
    // SeoService works in SSR because Meta service from @angular/platform-browser is SSR-safe
    effect(() => {
      const seoConfig = this.seoConfig();
      if (seoConfig) {
        this.seoService.updateSeoInformationApp(seoConfig);
      }
    });

    // Initialize push notifications when store configuration is ready
    // Only initialize once and only in browser environment
    let pushNotificationsInitialized = false;
    effect(() => {
      const storeReady = this.isStoreReady();
      const isBrowser = this.platformService.isBrowser();
      if (storeReady && !pushNotificationsInitialized && isBrowser) {
        pushNotificationsInitialized = true;
        this.pushNotificationsService.initializePushNotifications();
      }
    });

    // 🔧 FIX: Add scroll restoration functionality for route navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.seoService.addRobotsMeta();

        if (this.platformService.isBrowser()) {
          // Check if navigation state has scrollToTop as false
          const navigation = this.router.getCurrentNavigation();
          const scrollToTop = navigation?.extras?.state?.['scrollToTop'] ?? true;

          if (scrollToTop) {
            window.scrollTo(0, 0);
          }
        }
      });
  }

  // Listen for the scroll event on the window
  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Check if the scroll position is greater than or equal to 100px
    if (this.platformService.isBrowser()) {
      const scrollY = this.browserApiService.getPageYOffset();
      this._isScrolled.set(scrollY > 100);

      // Trigger infinite scroll when near bottom
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollBottom = scrollY + windowHeight;
      const distanceFromBottom = documentHeight - scrollBottom;

      // Trigger when within 300px of bottom
      if (distanceFromBottom < 300) {
        this.onScroll();
      }
    }
  }

  // Scroll to the top function
  scrollToTop() {
    this.browserApiService.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  ngOnInit(): void {
    this._isBrowser.set(this.platformService.isBrowser());

    // Manage UTM and code details from URL
    this.adsManagementService.manageUtmAndCodeDetailsFromUrl();

    // Auto login
    this.authenticationRepositoryService.autoLogin();

    // Push notifications are now initialized reactively via effect in constructor
    // This ensures they initialize when store configuration is ready
  }

  ngAfterViewInit(): void {
    this.injectScriptsService.init();

    if (this.platformService.isBrowser()) {
      requestAnimationFrame(() => {
        this.communicationService.setHeaderRef(this.header);
      });

      // Initialize modern infinite scroll
      this.setupInfiniteScroll();
      this.startInfiniteScrollObservation();
    }

    // Trigger smart preloading after initial load is complete
    this.preloadTimeoutId = setTimeout(() => {
      this.smartPreloadingStrategy.markInitialLoadComplete();
    }, 100);
  }

  logout() {
    this.authenticationRepositoryService.logout();
  }

  onScroll() {
    this.globalScrollService.scrolled();
  }

  // Modern infinite scroll implementation using Intersection Observer API
  private setupInfiniteScroll() {
    if (!this.platformService.isBrowser()) return;

    // Create intersection observer for infinite scroll
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.onScroll();
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: `${this.scrollThreshold}px`,
        threshold: 0.1
      }
    );
  }

  private startInfiniteScrollObservation() {
    if (this.intersectionObserver && this.scrollContainer?.nativeElement) {
      // Create a sentinel element at the bottom of the scroll container
      const sentinel = document.createElement('div');
      sentinel.style.height = '1px';
      sentinel.style.width = '100%';
      sentinel.setAttribute('data-infinite-scroll-sentinel', 'true');

      this.scrollContainer.nativeElement.appendChild(sentinel);
      this.intersectionObserver.observe(sentinel);
    }
  }

  private stopInfiniteScrollObservation() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
  private getUtmAndCodeDetailsFromUrl() {
    // This method is now called from effects in constructor
  }

  ngOnDestroy(): void {
    // Clear timeout to prevent memory leaks
    if (this.preloadTimeoutId) {
      clearTimeout(this.preloadTimeoutId);
      this.preloadTimeoutId = null;
    }
    // Disconnect intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}


